import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../AdminDashboard.css';
import NotificationCenter from '../components/NotificationCenter';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const AdminDashboard = ({ onLogout, setNotification }) => {
  const token = localStorage.getItem('token');
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [newWorker, setNewWorker] = useState({
    user: {
      username: '',
      email: '',
      password: '',
    },
    role: 'worker',
  });
  const [newService, setNewService] = useState({ name: '', description: '', price: '', duration: '' });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [revenueMetrics, setRevenueMetrics] = useState({
    total_bookings: 0,
    paid_bookings: 0,
    conversion_rate_percent: 0,
    cancellation_rate_percent: 0,
    captured_revenue_cents: 0,
    deposit_revenue_cents: 0,
    final_payment_revenue_cents: 0,
    refunded_amount_cents: 0,
    tip_revenue_cents: 0,
    net_revenue_cents: 0,
    expected_deposit_cents: 0,
    top_services: [],
  });
  const [ownerApplications, setOwnerApplications] = useState([]);
  const [workerApplications, setWorkerApplications] = useState([]);
  const [paymentOperations, setPaymentOperations] = useState([]);
  const [refundFormByBooking, setRefundFormByBooking] = useState({});
  const [submittingRefundId, setSubmittingRefundId] = useState(null);
  const [ownerApplicationNoteById, setOwnerApplicationNoteById] = useState({});
  const [workerApplicationNoteById, setWorkerApplicationNoteById] = useState({});
  const [isOwnerActionSubmitting, setIsOwnerActionSubmitting] = useState(false);
  const [isWorkerActionSubmitting, setIsWorkerActionSubmitting] = useState(false);
  const navigate = useNavigate();

  const showError = (message) => setFeedback({ type: 'error', message });
  const showSuccess = (message) => setFeedback({ type: 'success', message });

  const formatCurrencyFromCents = (cents) =>
    `$${((Number(cents || 0) || 0) / 100).toFixed(2)}`;

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const userResponse = await axios.get(apiUrl('userprofiles/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(userResponse.data);

        const serviceResponse = await axios.get(apiUrl('services/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setServices(serviceResponse.data);

        const bookingResponse = await axios.get(apiUrl('bookings/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBookings(bookingResponse.data);

        const revenueResponse = await axios.get(apiUrl('bookings/admin_revenue_metrics/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRevenueMetrics(revenueResponse.data || {});

        const paymentOperationsResponse = await axios.get(apiUrl('bookings/payment_operations/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPaymentOperations(Array.isArray(paymentOperationsResponse.data) ? paymentOperationsResponse.data : []);

        const categoryResponse = await axios.get(apiUrl('categories/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(categoryResponse.data);

        const ownerApplicationsResponse = await axios.get(
          apiUrl('userprofiles/admin/owner-applications/'),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setOwnerApplications(Array.isArray(ownerApplicationsResponse.data) ? ownerApplicationsResponse.data : []);

        const workerApplicationsResponse = await axios.get(
          apiUrl('userprofiles/worker-applications/review-queue/'),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setWorkerApplications(Array.isArray(workerApplicationsResponse.data) ? workerApplicationsResponse.data : []);
        setFeedback({ type: '', message: '' });

        setLoading(false);
      } catch (error) {
        showError('Error fetching data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    onLogout?.();
    navigate('/login');
  };

  const handleDeleteUser = async (id) => {
    try {
      await axios.delete(apiUrl(`userprofiles/${id}/`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(users.filter(user => user.id !== id));
      showSuccess('User deleted successfully.');
    } catch (error) {
      showError('Error deleting user.');
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        apiUrl('userprofiles/'),
        newWorker,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers([...users, response.data]);
      setNewWorker({
        user: { username: '', email: '', password: '' },
        role: 'worker',
      });
      showSuccess('Worker added successfully.');
    } catch (error) {
      showError('Error adding worker. Please try again.');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim() || !newCategory.description.trim()) return;

    try {
      const response = await axios.post(
        apiUrl('categories/'),
        newCategory,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories([...categories, response.data]);
      setNewCategory({ name: '', description: '' });
      showSuccess('Category added successfully.');
    } catch (error) {
      showError('Error adding category.');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(apiUrl(`categories/${id}/`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(categories.filter(category => category.id !== id));
      showSuccess('Category deleted successfully.');
    } catch (error) {
      showError('Error deleting category.');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.name.trim() || !newService.description.trim() || !newService.price || !newService.duration) {
      showError('All fields are required: Name, Description, Price, Duration.');
      return;
    }

    try {
      const response = await axios.post(
        apiUrl('services/'),
        JSON.stringify(newService),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setServices([...services, response.data]);
      showSuccess('Service added successfully!');
      setNewService({ name: '', description: '', price: '', duration: '' });
    } catch (error) {
      showError('Error adding service. Please try again.');
    }
  };

  const handleDeleteService = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(apiUrl(`services/${id}/`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServices(services.filter(service => service.id !== id));
      showSuccess('Service deleted successfully.');
    } catch (error) {
      console.error('Error deleting service:', error.response?.data || error.message);
      showError('Error deleting service.');
    }
  };
  

  const handleDeleteBooking = async (id) => {
    try {
      await axios.delete(apiUrl(`bookings/${id}/`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(bookings.filter(booking => booking.id !== id));
      showSuccess('Booking deleted successfully.');
    } catch (error) {
      showError('Error deleting booking.');
    }
  };

  const handleRefundFieldChange = (bookingId, field, value) => {
    setRefundFormByBooking((previous) => ({
      ...previous,
      [bookingId]: { ...(previous[bookingId] || {}), [field]: value },
    }));
  };

  const handleRefundPayment = async (booking) => {
    const form = refundFormByBooking[booking.id] || {};
    const reason = String(form.reason || '').trim();
    if (!reason) {
      showError('Enter a refund reason so the action has a complete audit trail.');
      return;
    }

    const refundableCents = Number(booking.payment_summary?.refundable_amount_cents || 0);
    const amountCents = form.amount
      ? Math.round(Number(form.amount) * 100)
      : refundableCents;
    if (!Number.isFinite(amountCents) || amountCents <= 0 || amountCents > refundableCents) {
      showError(`Refund must be between $0.01 and ${formatCurrencyFromCents(refundableCents)}.`);
      return;
    }

    try {
      setSubmittingRefundId(booking.id);
      const response = await axios.post(
        apiUrl(`bookings/${booking.id}/refund_payment/`),
        {
          reason,
          amount_cents: amountCents,
          payment_id: booking.payment_summary?.refundable_payment_id || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const operation = response.data;
      setPaymentOperations((previous) => [
        operation,
        ...previous.filter((item) => item.id !== operation.id),
      ]);
      setBookings((previous) => previous.map((item) => (
        item.id === booking.id
          ? {
              ...item,
              payment_summary: {
                ...item.payment_summary,
                refund_status: operation.status,
                refunded_amount_cents: operation.status === 'succeeded'
                  ? Number(item.payment_summary?.refunded_amount_cents || 0) + operation.amount_cents
                  : Number(item.payment_summary?.refunded_amount_cents || 0),
                refundable_amount_cents: operation.status === 'succeeded'
                  ? Math.max(Number(item.payment_summary?.refundable_amount_cents || 0) - operation.amount_cents, 0)
                  : Number(item.payment_summary?.refundable_amount_cents || 0),
              },
            }
          : item
      )));
      setRefundFormByBooking((previous) => ({ ...previous, [booking.id]: { amount: '', reason: '' } }));
      showSuccess(operation.status === 'succeeded' ? 'Stripe confirmed the refund.' : 'Refund submitted and awaiting Stripe confirmation.');
    } catch (error) {
      const apiError = error?.response?.data?.error;
      const failedOperation = error?.response?.data?.operation;
      if (failedOperation) {
        setPaymentOperations((previous) => [
          failedOperation,
          ...previous.filter((item) => item.id !== failedOperation.id),
        ]);
      }
      showError((typeof apiError === 'string' && apiError.trim()) ? apiError : 'Unable to process the refund.');
    } finally {
      setSubmittingRefundId(null);
    }
  };

  const handleReviewOwnerApplication = async (applicationId, actionName) => {
    try {
      setIsOwnerActionSubmitting(true);
      const review_note = ownerApplicationNoteById[applicationId] || '';
      const response = await axios.post(
        apiUrl(`userprofiles/admin/owner-applications/${applicationId}/review/`),
        {
          action: actionName,
          review_note,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOwnerApplications((prev) => prev.map((row) => (
        row.id === applicationId ? response.data : row
      )));
      showSuccess(`Owner application ${actionName}d successfully.`);
    } catch (error) {
      const apiError = error?.response?.data?.error;
      showError((typeof apiError === 'string' && apiError.trim()) ? apiError : 'Failed to review owner application.');
    } finally {
      setIsOwnerActionSubmitting(false);
    }
  };

  const handleReviewWorkerApplication = async (applicationId, actionName) => {
    try {
      setIsWorkerActionSubmitting(true);
      const review_note = workerApplicationNoteById[applicationId] || '';
      const response = await axios.post(
        apiUrl(`userprofiles/worker-applications/${applicationId}/review/`),
        {
          action: actionName,
          review_note,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setWorkerApplications((prev) => prev.map((row) => (
        row.id === applicationId ? response.data : row
      )));
      showSuccess(`Worker application ${actionName}d successfully.`);
    } catch (error) {
      const apiError = error?.response?.data?.error;
      showError((typeof apiError === 'string' && apiError.trim()) ? apiError : 'Failed to review worker application.');
    } finally {
      setIsWorkerActionSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <InlineFeedback type="info" message="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <NotificationCenter embedded setNotification={setNotification} />
        <button onClick={() => navigate('/calendar')} className="btn-calendar">Booking Calendar</button>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </header>

      <InlineFeedback type={feedback.type || 'info'} message={feedback.message} className="dashboard-message" />

      <section className="dashboard-section">
        <h2>Revenue Overview</h2>
        <div className="card-container">
          <div className="card">
            <h3>Captured Revenue</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.captured_revenue_cents)}</p>
          </div>
          <div className="card">
            <h3>Final Balances</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.final_payment_revenue_cents)}</p>
          </div>
          <div className="card">
            <h3>Refunded</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.refunded_amount_cents)}</p>
          </div>
          <div className="card">
            <h3>Tips</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.tip_revenue_cents)}</p>
          </div>
          <div className="card">
            <h3>Net Revenue</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.net_revenue_cents)}</p>
          </div>
          <div className="card">
            <h3>Expected Deposits</h3>
            <p>{formatCurrencyFromCents(revenueMetrics.expected_deposit_cents)}</p>
          </div>
          <div className="card">
            <h3>Paid Booking Conversion</h3>
            <p>{Number(revenueMetrics.conversion_rate_percent || 0).toFixed(2)}%</p>
          </div>
          <div className="card">
            <h3>Cancellation Rate</h3>
            <p>{Number(revenueMetrics.cancellation_rate_percent || 0).toFixed(2)}%</p>
          </div>
        </div>
        {Array.isArray(revenueMetrics.top_services) && revenueMetrics.top_services.length > 0 ? (
          <div className="card-container">
            {revenueMetrics.top_services.map((item) => (
              <div key={item.service_id} className="card">
                <h3>{item.name}</h3>
                <p>Bookings: {item.booking_count}</p>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="dashboard-section payment-operations-section">
        <div className="section-heading-row">
          <div>
            <h2>Payment Operations</h2>
            <p className="section-help">Canceled paid bookings enter this audited queue. Refunds are sent to Stripe only after an admin confirms them.</p>
          </div>
          <span className="queue-count">{paymentOperations.filter((item) => item.status !== 'succeeded').length} open</span>
        </div>

        {paymentOperations.length === 0 ? (
          <div className="empty-payment-state">No refund operations yet.</div>
        ) : (
          <div className="payment-operation-list">
            {paymentOperations.map((operation) => {
              const booking = bookings.find((item) => item.id === operation.booking);
              const form = refundFormByBooking[operation.booking] || {};
              const canProcess = operation.status === 'pending' || operation.status === 'failed';
              return (
                <article key={operation.id} className={`payment-operation-card status-${operation.status}`}>
                  <div className="payment-operation-summary">
                    <div>
                      <span className="operation-label">Booking #{operation.booking}</span>
                      <h3>{formatCurrencyFromCents(operation.amount_cents)} refund</h3>
                      <p>{operation.reason}</p>
                    </div>
                    <span className="operation-status">{operation.status}</span>
                  </div>
                  <div className="operation-meta">
                    <span>Client: {booking?.client?.user?.username || booking?.client?.first_name || 'Unknown'}</span>
                    <span>Attempts: {operation.attempt_count}</span>
                    {operation.stripe_refund_id ? <span>Stripe: {operation.stripe_refund_id}</span> : null}
                  </div>
                  {operation.failure_reason ? <p className="operation-error">{operation.failure_reason}</p> : null}
                  {canProcess && booking ? (
                    <div className="refund-actions">
                      <label htmlFor={`refund-reason-${booking.id}`}>Admin refund note</label>
                      <textarea
                        id={`refund-reason-${booking.id}`}
                        value={form.reason || operation.reason || ''}
                        onChange={(event) => handleRefundFieldChange(booking.id, 'reason', event.target.value)}
                        placeholder="Why this refund is being approved"
                      />
                      <button
                        type="button"
                        className="btn-refund"
                        disabled={submittingRefundId === booking.id}
                        onClick={() => handleRefundPayment(booking)}
                      >
                        {submittingRefundId === booking.id ? 'Contacting Stripe…' : operation.status === 'failed' ? 'Retry Refund' : 'Approve Refund'}
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="dashboard-section">
        <h2>Add Worker</h2>
        <form onSubmit={handleAddWorker} className="form">
          <label htmlFor="new-worker-username" className="sr-only">Worker username</label>
          <input id="new-worker-username" type="text" placeholder="Username" value={newWorker.user.username} onChange={(e) => setNewWorker({ ...newWorker, user: { ...newWorker.user, username: e.target.value } })} required />
          <label htmlFor="new-worker-email" className="sr-only">Worker email</label>
          <input id="new-worker-email" type="email" placeholder="Email" value={newWorker.user.email} onChange={(e) => setNewWorker({ ...newWorker, user: { ...newWorker.user, email: e.target.value } })} required />
          <div className="password-group">
            <label htmlFor="new-worker-password" className="sr-only">Worker password</label>
            <input id="new-worker-password" type={passwordVisible ? 'text' : 'password'} placeholder="Password" value={newWorker.user.password} onChange={(e) => setNewWorker({ ...newWorker, user: { ...newWorker.user, password: e.target.value } })} required />
            <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="btn-toggle-password">
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          <button type="submit" className="btn-add">Add Worker</button>
        </form>
      </section>

      <section className="dashboard-section">
        <h2>Add Service</h2>
        <form onSubmit={handleAddService} className="form">
          <label htmlFor="new-service-name" className="sr-only">Service name</label>
          <input id="new-service-name" type="text" placeholder="Service Name" value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} required />
          <label htmlFor="new-service-description" className="sr-only">Service description</label>
          <textarea id="new-service-description" placeholder="Service Description" value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} required />
          <label htmlFor="new-service-price" className="sr-only">Service price</label>
          <input id="new-service-price" type="number" placeholder="Price (e.g., 29.99)" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })} required />
          <label htmlFor="new-service-duration" className="sr-only">Service duration in minutes</label>
          <input id="new-service-duration" type="number" placeholder="Duration (in minutes)" value={newService.duration} onChange={(e) => setNewService({ ...newService, duration: e.target.value })} required />
          <button type="submit" className="btn-add">Add Service</button>
        </form>
      </section>

      <section className="dashboard-section">
        <h2>Top-Level Categories</h2>
        <form onSubmit={handleAddCategory} className="form">
          <label htmlFor="new-category-name" className="sr-only">Category name</label>
          <input id="new-category-name" type="text" placeholder="Category Name" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} required />
          <label htmlFor="new-category-description" className="sr-only">Category description</label>
          <textarea id="new-category-description" placeholder="Category Description" value={newCategory.description} onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })} required />
          <button type="submit" className="btn-add">Add Category</button>
        </form>
        <div className="card-container">
          {categories.map(cat => (
            <div key={cat.id} className="card">
              <h3>{cat.name}</h3>
              <p>{cat.description}</p>
              <button onClick={() => handleDeleteCategory(cat.id)} className="btn-delete">Delete</button>
            </div>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <h2>Owner Access Applications</h2>
        {ownerApplications.length === 0 ? (
          <p>No owner applications have been submitted yet.</p>
        ) : (
          <div className="card-container">
            {ownerApplications.map((application) => (
              <div key={application.id} className="card">
                <h3>{application.requested_company_name}</h3>
                <p>Applicant: {application.applicant_username}</p>
                <p>Business Email: {application.business_email}</p>
                <p>Status: {application.status}</p>
                {application.message ? <p>Message: {application.message}</p> : null}
                {application.review_note ? <p>Review Note: {application.review_note}</p> : null}

                {application.status === 'pending' ? (
                  <div className="form" style={{ marginTop: '0.5rem' }}>
                    <label htmlFor={`owner-app-note-${application.id}`} className="sr-only">Review note</label>
                    <textarea
                      id={`owner-app-note-${application.id}`}
                      placeholder="Optional note (required when rejecting)"
                      value={ownerApplicationNoteById[application.id] || ''}
                      onChange={(e) => setOwnerApplicationNoteById((prev) => ({
                        ...prev,
                        [application.id]: e.target.value,
                      }))}
                    />
                    <div className="password-group">
                      <button
                        type="button"
                        className="btn-add"
                        disabled={isOwnerActionSubmitting}
                        onClick={() => handleReviewOwnerApplication(application.id, 'approve')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="btn-delete"
                        disabled={isOwnerActionSubmitting}
                        onClick={() => handleReviewOwnerApplication(application.id, 'reject')}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dashboard-section">
        <h2>Worker Applications</h2>
        {workerApplications.length === 0 ? (
          <p>No worker applications have been submitted yet.</p>
        ) : (
          <div className="card-container">
            {workerApplications.map((application) => (
              <div key={application.id} className="card">
                <h3>{application.applicant_username}</h3>
                <p>Company: {application.requested_company_name}</p>
                <p>Professional Email: {application.professional_email}</p>
                <p>Status: {application.status}</p>
                {application.specialties ? <p>Specialties: {application.specialties}</p> : null}
                {application.message ? <p>Message: {application.message}</p> : null}
                {application.review_note ? <p>Review Note: {application.review_note}</p> : null}

                {application.status === 'pending' ? (
                  <div className="form" style={{ marginTop: '0.5rem' }}>
                    <label htmlFor={`worker-app-note-${application.id}`} className="sr-only">Worker review note</label>
                    <textarea
                      id={`worker-app-note-${application.id}`}
                      placeholder="Optional note (required when rejecting)"
                      value={workerApplicationNoteById[application.id] || ''}
                      onChange={(e) => setWorkerApplicationNoteById((prev) => ({
                        ...prev,
                        [application.id]: e.target.value,
                      }))}
                    />
                    <div className="password-group">
                      <button
                        type="button"
                        className="btn-add"
                        disabled={isWorkerActionSubmitting}
                        onClick={() => handleReviewWorkerApplication(application.id, 'approve')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="btn-delete"
                        disabled={isWorkerActionSubmitting}
                        onClick={() => handleReviewWorkerApplication(application.id, 'reject')}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dashboard-section">
        <h2>Users</h2>
        <div className="card-container">
          {users.map(user => (
            <div key={user.id} className="card">
              <h3>{user.user.username}</h3>
              <p>Role: {user.role || 'No role assigned'}</p>
              <button onClick={() => handleDeleteUser(user.id)} className="btn-delete">Delete</button>
            </div>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <h2>Existing Services</h2>
        <div className="card-container">
          {services.map(service => (
            <div key={service.id} className="card">
              <h3>{service.name}</h3>
              <p>{service.description}</p>
              <button onClick={() => handleDeleteService(service.id)} className="btn-delete">Delete</button>
            </div>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <h2>Bookings</h2>
        <div className="card-container">
          {bookings.map(booking => (
            <div key={booking.id} className="card">
              <h3>{booking.name}</h3>
              <p>Date: {booking.date}</p>
              <p>Time: {booking.time}</p>
              <p>Payment: {booking.payment_summary?.status || 'not required'}</p>
              {Number(booking.payment_summary?.refundable_amount_cents || 0) > 0 ? (
                <div className="refund-actions">
                  <p><strong>Refundable:</strong> {formatCurrencyFromCents(booking.payment_summary.refundable_amount_cents)}</p>
                  <label htmlFor={`manual-refund-amount-${booking.id}`}>Refund amount</label>
                  <input
                    id={`manual-refund-amount-${booking.id}`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={(booking.payment_summary.refundable_amount_cents / 100).toFixed(2)}
                    placeholder={(booking.payment_summary.refundable_amount_cents / 100).toFixed(2)}
                    value={refundFormByBooking[booking.id]?.amount || ''}
                    onChange={(event) => handleRefundFieldChange(booking.id, 'amount', event.target.value)}
                  />
                  <label htmlFor={`manual-refund-reason-${booking.id}`}>Refund reason</label>
                  <textarea
                    id={`manual-refund-reason-${booking.id}`}
                    placeholder="Required audit note"
                    value={refundFormByBooking[booking.id]?.reason || ''}
                    onChange={(event) => handleRefundFieldChange(booking.id, 'reason', event.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-refund"
                    disabled={submittingRefundId === booking.id}
                    onClick={() => handleRefundPayment(booking)}
                  >
                    {submittingRefundId === booking.id ? 'Contacting Stripe…' : 'Issue Refund'}
                  </button>
                </div>
              ) : null}
              <button onClick={() => handleDeleteBooking(booking.id)} className="btn-delete">Delete</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
